import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import React, { useState } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import type { VideoAsset } from '../../../services/admin/adminBackendServiceVideoLibraryMixin';
import DeleteVideoDialog from './DeleteVideoDialog';
import VideoLibraryCard from './VideoLibraryCard';
import { LIBRARY_COLUMN_MAX_WIDTH } from './libraryLayout';
import VideoLibrarySearchField from './VideoLibrarySearchField';
import VideoMetadataEditDialog from './VideoMetadataEditDialog';
import VideoUploadForm from './VideoUploadForm';
import { useVideoLibrary } from './useVideoLibrary';

interface VideoLibraryPanelProps {
  fundOpportunityId: string;
  backendService: BackendService;
}

function VideoLibraryGrid({
  loading,
  videos,
  onDelete,
  onEdit,
}: {
  loading: boolean;
  videos: VideoAsset[];
  onDelete: (video: VideoAsset) => void;
  onEdit: (video: VideoAsset) => void;
}) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (videos.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No videos yet — upload one above.
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      {videos.map((video) => (
        <VideoLibraryCard
          key={video.externalId}
          video={video}
          onDelete={() => onDelete(video)}
          onEdit={() => onEdit(video)}
        />
      ))}
    </Box>
  );
}

export default function VideoLibraryPanel({
  fundOpportunityId,
  backendService,
}: VideoLibraryPanelProps) {
  const library = useVideoLibrary({ fundOpportunityId, backendService });
  const [pendingDelete, setPendingDelete] = useState<VideoAsset | null>(null);
  const [editing, setEditing] = useState<VideoAsset | null>(null);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: LIBRARY_COLUMN_MAX_WIDTH,
      }}
    >
      <Typography variant="h6">Video Library</Typography>
      <Typography variant="body2" color="text.secondary">
        Upload a video once, then reuse it in any fund opportunity&apos;s content. Videos are
        compressed for web streaming after upload.
      </Typography>

      <VideoUploadForm
        uploading={library.uploading}
        uploadError={library.uploadError}
        onDismissError={library.dismissUploadError}
        onUpload={library.upload}
      />

      {library.isProcessing && (
        <Alert severity="info" sx={{ py: 0 }}>
          A video below is still compressing. Please wait for completion before publishing content
          that uses it.
        </Alert>
      )}

      <VideoLibrarySearchField fullWidth value={library.search} onChange={library.setSearch} />

      {library.error && (
        <Typography variant="caption" color="error.main">
          {library.error}
        </Typography>
      )}

      <VideoLibraryGrid
        loading={library.loading}
        videos={library.videos}
        onDelete={setPendingDelete}
        onEdit={setEditing}
      />

      <VideoMetadataEditDialog
        video={editing}
        onCancel={() => setEditing(null)}
        onSave={async (externalId, fields) => {
          await library.updateMetadata(externalId, fields);
          setEditing(null);
        }}
      />

      <DeleteVideoDialog
        video={pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={(force) => {
          const video = pendingDelete!;
          setPendingDelete(null);
          void library.remove(video.externalId, force);
        }}
      />
    </Box>
  );
}
